import { Intent } from "@blueprintjs/core";
import { LinkButton } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormLocationSelector, FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { EmployeeStatus } from "components/status";
import { Tooltip } from "components/tooltip";
import { UserHasAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce";
import { useQuery } from "hooks/useQuery";
import { awaitingSignOffStatusBand, completeSignOffStatusBand, getEditBlockedMessage } from "pages/employee-reviews/shared";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { EmployeeReviewsService, EmployeeService } from "services";
import { SetFiltering } from "state/actions/Actions";

const outcomeLevelSectionKey = "OUTCOMELEVEL";

const signOffStatuses = {
    AwaitingSignOff: awaitingSignOffStatusBand,
    Complete: completeSignOffStatusBand
};

const signOffStatusFilterItems = Object.entries(signOffStatuses).map(([id, status]) => ({ id: id, name: status.label }));

// Completed By and Sign Off Status are absent from sortableHeaders because the API
// offers no sort field for either.
const tableHeaders = ["Employee", "Location", "Review Type", "Completed", "Completed By", "Raised By", "Outcome Level", "Sign Off Status"];
// The edit action takes a last column of its own for users who can edit reviews.
const editActionHeaders = [...tableHeaders, "Actions"];
const performAction = "EmployeeReview:Perform";
const sortableHeaders = [
    { headerName: "Employee", sortName: "employee" },
    { headerName: "Location", sortName: "location" },
    { headerName: "Review Type", sortName: "reviewtype" },
    { headerName: "Completed", sortName: "completeddate" },
    { headerName: "Raised By", sortName: "raisedby" },
    { headerName: "Outcome Level", sortName: "outcomelevel" }
];

// Completed reviews list most-recently-completed first by default.
const defaultSortHeader = sortableHeaders.find((header) => header.sortName === "completeddate");

function renderOutcomeLevel(outcomeLevel) {
    if (outcomeLevel == null) {
        return "-";
    }

    return `Level ${outcomeLevel.number} - ${outcomeLevel.name}`;
}

function renderSignOffStatus(signOff) {
    if (signOff?.status == null) {
        return "-";
    }

    const status = signOffStatuses[signOff.status];

    if (status == null) {
        return signOff.status;
    }

    return <span key={`sign-off-${signOff.status}`} className={status.className}>{status.label}</span>;
}

// canEdit is decided per row by the search. A row the user cannot edit keeps the action, disabled,
// with the reason the search gives. A disabled button fires no hover events, so a wrapping span
// gives the tooltip its target.
function renderEditAction(item) {
    const canEdit = item.canEdit === true;
    let tooltip = "Edit";

    if (!canEdit) {
        tooltip = getEditBlockedMessage(item.editBlockedReason);
    }

    return (
        <Tooltip key={`edit-review-${item.reviewId}`} content={tooltip}>
            <span>
                <LinkButton
                    id={`edit-review-${item.reviewId}`}
                    icon="edit"
                    iconOnly
                    minimal
                    large={false}
                    disabled={!canEdit}
                    href={`/employee-reviews/completed-reviews/${item.reviewId}/edit`}
                />
            </span>
        </Tooltip>
    );
}

export function EmployeeReviewsCompleted() {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = "employeeReviewsCompleted";

    const dispatch = useDispatch();
    const query = useQuery();
    const queryOutcomeLevel = query.get("outcomeLevel");
    const querySignOffStatus = query.get("signOffStatus");
    // The month drill-down from the overview bounds the search by completed date;
    // both bounds go straight into the request without appearing in the funnel UI.
    const queryCompletedAfter = query.get("completedAfter");
    const queryCompletedBefore = query.get("completedBefore");
    const canEditReviews = UserHasAccess(performAction);

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pageFilters, setPageFilters] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);

    const [sortedDir, setSortedDir] = useState("D");
    const [sortedBy, setSortedBy] = useState(defaultSortHeader);

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableRows, setTableRows] = useState([]);

    const [appliedFilters, setAppliedFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const reduxLocations = useSelector((state) => state.employeeReviews.locations);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const locationFilterOpen = useRef(false);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();

    const selectedFilters = useSelector((state) => state.filters[filterName]);

    const noDataMessage = "There are no completed reviews for the selected locations and filters.";

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []));
    }

    // The outcome-level and sign-off-status drill-throughs both seed real funnel filters
    // rather than request-only parameters, so arriving from either tile resets the page's
    // persisted selections instead of combining with them.
    function buildDrillThroughFilters(outcomeLevelItems) {
        const drillThroughFilters = [];

        if (queryOutcomeLevel != null && queryOutcomeLevel !== "") {
            const matchingItem = outcomeLevelItems.find((item) => item.id === queryOutcomeLevel);

            drillThroughFilters.push({
                name: "OutcomeLevel",
                key: "OutcomeLevel",
                value: queryOutcomeLevel,
                displayName: matchingItem?.name ?? queryOutcomeLevel
            });
        }

        // Only a status the funnel itself offers is seeded. The seeded filter is persisted,
        // so a value the search does not accept would 400 every later search until the user
        // cleared it by hand; an unrecognised one is ignored as though it were absent.
        const matchingSignOffStatus = signOffStatusFilterItems.find((item) => item.id === querySignOffStatus);

        if (matchingSignOffStatus != null) {
            drillThroughFilters.push({
                name: "SignOffStatus",
                key: "SignOffStatus",
                value: matchingSignOffStatus.id,
                displayName: matchingSignOffStatus.name
            });
        }

        return drillThroughFilters;
    }

    useEffect(() => {
        Promise.all([
            EmployeeReviewsService.getOptions(),
            EmployeeService.getEmployeeOptions()
        ])
            .then(([options, employeeOptions]) => {
                setEmployeeStatusOptions(employeeOptions?.employeeStatuses ?? []);
                setEmployeeTypeOptions(employeeOptions?.employeeTypes ?? []);

                const outcomeLevelItems = (options?.statsSegments ?? [])
                    .filter((segment) => segment.sectionKey === outcomeLevelSectionKey)
                    .sort((a, b) => a.order - b.order)
                    .map((segment) => ({ id: segment.filterValues[0], name: segment.name }));

                setPageFilters([
                    {
                        name: "ReviewType",
                        displayName: "Review Type",
                        items: (options?.reviewTypes ?? []).map((reviewType) => ({ id: reviewType.id.toString(), name: reviewType.name }))
                    },
                    {
                        name: "OutcomeLevel",
                        displayName: "Outcome Level",
                        items: outcomeLevelItems
                    },
                    {
                        name: "SignOffStatus",
                        displayName: "Sign Off Status",
                        // The search rejects more than one sign-off status value with a 400,
                        // so this group has to be single-select rather than the default.
                        singleSelect: true,
                        items: signOffStatusFilterItems
                    },
                    {
                        name: "RaisedBy",
                        displayName: "Raised By",
                        items: (options?.raisedBy ?? []).map((raisedBy) => ({ id: raisedBy.id.toString(), name: raisedBy.name }))
                    }
                ]);

                // Apply the overview drill-through in the same render as initalSetupComplete,
                // so the first search already carries the seeded filters. A drill-down defines
                // the entire funnel state, so any drill-through param — including the
                // request-only date bounds — replaces the persisted selections.
                const hasDateDrillThrough =
                    (queryCompletedAfter != null && queryCompletedAfter !== "") ||
                    (queryCompletedBefore != null && queryCompletedBefore !== "");

                const drillThroughFilters = buildDrillThroughFilters(outcomeLevelItems);
                if (drillThroughFilters.length > 0 || hasDateDrillThrough) {
                    setAppliedFilters(drillThroughFilters);
                    dispatch(SetFiltering(filterName, drillThroughFilters));
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to load review options. Filter choices may be incomplete.");
            })
            .finally(() => {
                setInitalSetupComplete(true);
            });
    }, []);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }

        if (locationFilterOpen.current) {
            return;
        }

        fetchTableData(false);
    }, [reduxLocations]);

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_REVIEWS_LOCATIONS",
            payload: locations
        });
    }

    function onLocationFilterOpened() {
        locationFilterOpen.current = true;
    }

    function onLocationFilterClosed() {
        locationFilterOpen.current = false;
        fetchTableData(false);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => !prevState);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
        setPageNumber(1);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map((f) => ({ key: f.name, value: f.value }));

        if (reduxLocations != null) {
            const mappedLocations = reduxLocations.map((locationId) => ({ key: "Location", value: locationId }));
            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        if (queryCompletedAfter != null && queryCompletedAfter !== "") {
            mappedFilters.push({ key: "CompletedAfter", value: queryCompletedAfter });
        }

        if (queryCompletedBefore != null && queryCompletedBefore !== "") {
            mappedFilters.push({ key: "CompletedBefore", value: queryCompletedBefore });
        }

        return mappedFilters;
    }

    function buildRows(items) {
        return items.map((item) => {
            const cells = buildRowCells(item);

            if (canEditReviews) {
                cells.push(renderEditAction(item));
            }

            return cells;
        });
    }

    function buildRowCells(item) {
        return [
            <div key={`completed-employee-${item.employee.id}`} className="inline-items">
                <EmployeeStatus
                    statusId={item.employee.status?.id}
                    typeId={item.employee.type?.id}
                    typeAttributeMappings={employeeTypeOptions}
                    statusAttributeMappings={employeeStatusOptions}
                />
                <Link to={`/employee-reviews/completed-reviews/${item.reviewId}`}>{item.employee.name}</Link>
            </div>,
            item.location,
            item.reviewType,
            <DateToLocal key={`completed-date-${item.employee.id}`} format="DD/MM/YYYY">{item.completedDate}</DateToLocal>,
            item.completedBy,
            item.raisedBy,
            renderOutcomeLevel(item.outcomeLevel),
            renderSignOffStatus(item.signOff)
        ];
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentTableSortDir.current === sortedDir &&
                currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentTableSortBy.current === sortedBy &&
                currentAppliedFilters.current === appliedFilters &&
                currentSearchTerm.current === debouncedSearchTerm &&
                // Compare the location contents, not the count, so swapping one
                // location for another still triggers a refetch.
                currentLocations.current === reduxLocations.join(',') &&
                !forceRefresh
            ) {
                // If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentTableSortBy.current = sortedBy;
            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentTableSortDir.current = sortedDir;
            currentAppliedFilters.current = appliedFilters;
            currentSearchTerm.current = debouncedSearchTerm;
            currentLocations.current = reduxLocations.join(',');

            setIsLoading(true);

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                searchTerm: debouncedSearchTerm,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mapFilters()
            };

            EmployeeReviewsService.searchCompletedReviews(searchRequest)
                .then((r) => {
                    setTableRows(r?.data != null ? buildRows(r.data) : []);
                    setTotalRecords(r?.totalCount ?? 0);
                    setIsLoading(false);
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Failed to get completed reviews. Please refresh the page.");
                    setIsLoading(false);
                });
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        pageSize,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters
    ]);

    return (
        <>
            <div className="filter-controls spacer-bottom">
                <div className="inline-items">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search employees"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="completed-reviews-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={!initalSetupComplete}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListOpened={onLocationFilterOpened}
                            onLocationListClose={onLocationFilterClosed}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                </div>
                <Filter
                    filterName={filterName}
                    filters={pageFilters}
                    visible={showFilters}
                    numOfColumns={3}
                    onUpdate={onFilterChange}
                    onClickOutside={onToggleFilter}
                />
            </div>

            <ListingTable
                id="completed-reviews-listing"
                pageSize={pageSize}
                headers={canEditReviews ? editActionHeaders : tableHeaders}
                data={tableRows}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                totalRecordCount={totalRecords}
                noDataMessage={noDataMessage}
                sortable
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                sortableHeaders={sortableHeaders}
                onSort={onTableSort}
            />
        </>
    );
}
